import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AiSummaryContextLevel, AiSummaryLength } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';
import { Defaults } from 'webapp/models-custom/visualization/ai-summary';

interface Args {
	blockM: Block;
}

export default class BlockSettingsAiSummaryComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;

	@tracked showTrend = false;
	@tracked language = '';
	@tracked contextLevel = Defaults.aiSummaryContextLevelType;
	@tracked length = Defaults.aiSummaryLengthType;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updatePropertiesFromBlock();
	}

	contextLevelOptions = [
		{ value: AiSummaryContextLevel.FACTUAL, label: 'Data-focused' },
		{ value: AiSummaryContextLevel.BALANCED, label: 'Balanced' },
		{ value: AiSummaryContextLevel.CONTEXTUAL, label: 'Descriptive' },
	];

	lengthOptions = [
		{ value: AiSummaryLength.SHORT, label: 'Shorter' },
		{ value: AiSummaryLength.NORMAL, label: 'Normal' },
		{ value: AiSummaryLength.LONG, label: 'Longer' },
	];

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization as VisualizationAiSummary;
	}

	get languageOptions() {
		return this.constantsService.aiSummaryLanguages;
	}

	get isOnTemplate(): boolean {
		return this.block.isOnTemplate;
	}

	@action
	onBlockChange() {
		this.updatePropertiesFromBlock();
	}

	@action
	changeAiSummaryShowTrend(selected: boolean) {
		const originalValue = this.showTrend;
		this.showTrend = selected;

		const applyChange = () => {
			this.block.aiSummaryShowTrend = selected;
			this.block.setAiSummaryEditInfoToNull();
			this.block.blockSetup.saveBlock();
			this.blockSetup.trackBlockPropertySettingsChangeEvent({
				performance_indicator: selected,
			});
		};

		if (this.isOnTemplate) {
			applyChange();
		} else {
			this.visualization.showRegenerationWarning(applyChange, () => {
				this.showTrend = originalValue;
			});
		}
	}

	@action
	changeLanguage(selected: string) {
		const originalValue = this.language;
		this.language = selected;

		const applyChange = () => {
			this.block.aiSummaryLanguage = selected;
			this.block.setAiSummaryEditInfoToNull();
			this.block.blockSetup.saveBlock();
			this.blockSetup.trackBlockPropertySettingsChangeEvent({ language: selected });
		};

		if (this.isOnTemplate) {
			applyChange();
		} else {
			this.visualization.showRegenerationWarning(applyChange, () => {
				this.language = originalValue;
			});
		}
	}

	@action
	changeContextLevel(value: AnySimpleValue) {
		const selected = value as AiSummaryContextLevel;
		const originalValue = this.contextLevel;
		this.contextLevel = selected;

		const applyChange = () => {
			this.block.aiSummaryContextLevelType = selected;
			this.block.setAiSummaryEditInfoToNull();
			this.block.blockSetup.saveBlock();

			// tracking
			let narrative_style = '';
			switch (selected) {
				case AiSummaryContextLevel.BALANCED:
					narrative_style = 'balanced';
					break;
				case AiSummaryContextLevel.FACTUAL:
					narrative_style = 'data';
					break;
				case AiSummaryContextLevel.CONTEXTUAL:
					narrative_style = 'descriptive';
					break;
			}

			this.blockSetup.trackBlockPropertySettingsChangeEvent({ narrative_style });
		};

		if (this.isOnTemplate) {
			applyChange();
		} else {
			this.visualization.showRegenerationWarning(applyChange, () => {
				this.contextLevel = originalValue;
			});
		}
	}

	@action
	changeLength(value: AnySimpleValue) {
		const selected = value as AiSummaryLength;
		const originalValue = this.length;
		this.length = selected;

		const applyChange = () => {
			this.block.aiSummaryLengthType = selected;
			this.block.setAiSummaryEditInfoToNull();
			this.block.blockSetup.saveBlock();

			// tracking
			let length = '';
			switch (selected) {
				case AiSummaryLength.NORMAL:
					length = 'medium';
					break;
				case AiSummaryLength.SHORT:
					length = 'short';
					break;
				case AiSummaryLength.LONG:
					length = 'long';
					break;
			}
			this.blockSetup.trackBlockPropertySettingsChangeEvent({ length });
		};

		if (this.isOnTemplate) {
			applyChange();
		} else {
			this.visualization.showRegenerationWarning(applyChange, () => {
				this.length = originalValue;
			});
		}
	}

	private updatePropertiesFromBlock() {
		const block = this.block;

		this.showTrend = block.aiSummaryShowTrend;
		this.language = block.aiSummaryLanguage;
		this.contextLevel = block.aiSummaryContextLevelType ?? Defaults.aiSummaryContextLevelType;
		this.length = block.aiSummaryLengthType ?? Defaults.aiSummaryLengthType;
	}
	<template>
		<div
			class="row row--toggle"
			{{didUpdate this.onBlockChange @blockM this.block.aiSummaryShowTrend}}
		>
			<FormCheckboxSwitchComponent
				@name="show-trend"
				@selected={{this.showTrend}}
				@onChange={{this.changeAiSummaryShowTrend}}
			/>
			<label>Show performance indicator</label>
		</div>

		<div class="row">
			<label>Language</label>

			<FormSelectComponent
				name="language"
				@selected={{this.language}}
				@options={{this.languageOptions}}
				@onChange={{this.changeLanguage}}
				@isNumeric={{false}}
				@isRequired={{true}}
				@theme="dark"
			/>

		</div>

		<div class="row">
			<label>Narrative style</label>

			<FormRadiobuttonGroupComponent
				class="radiobutton-switcher"
				@name="context-level"
				@selected={{this.contextLevel}}
				@options={{this.contextLevelOptions}}
				@onChange={{this.changeContextLevel}}
				@theme="dark-blue"
			/>

		</div>

		<div class="row">
			<label>Summary length</label>

			<FormRadiobuttonGroupComponent
				class="radiobutton-switcher"
				@name="length"
				@selected={{this.length}}
				@options={{this.lengthOptions}}
				@onChange={{this.changeLength}}
				@theme="dark-blue"
			/>

		</div>
	</template>
}
